import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const HOLD_DELAY_MS = 180; // bu süreden kısa basışlar "tıklama" (lightbox), uzunlar "basılı tutma" (büyüteç) sayılır
const LENS_SIZE = 150; // büyüteç merceğinin çapı (px)
const ZOOM_FACTOR = 2.5;

/**
 * Yüklenen fotoğrafları göstermek için global bileşen:
 *  - Tıklama (hızlı bas-bırak) -> tam ekran lightbox açar
 *  - Sol tık basılı tutma -> imleci takip eden büyüteç merceği gösterir
 * className, normal <img> gibi boyut/stil vermek için kullanılır.
 */
export default function ZoomableImage({ src, alt = "", className = "" }) {
  const imgRef = useRef(null);
  const holdTimer = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [magnifierActive, setMagnifierActive] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [bgPos, setBgPos] = useState({ x: 0, y: 0 });
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

  const clearHoldTimer = () => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  const updateLens = (clientX, clientY) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
    setLensPos({ x, y });
    setImgSize({ w: rect.width, h: rect.height });
    setBgPos({
      x: -(x * ZOOM_FACTOR - LENS_SIZE / 2),
      y: -(y * ZOOM_FACTOR - LENS_SIZE / 2),
    });
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // yalnızca sol tık
    e.preventDefault();
    clearHoldTimer();
    holdTimer.current = setTimeout(() => {
      setMagnifierActive(true);
      updateLens(e.clientX, e.clientY);
    }, HOLD_DELAY_MS);
  };

  const handleMouseMove = (e) => {
    if (magnifierActive) updateLens(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    const wasHolding = magnifierActive;
    clearHoldTimer();
    setMagnifierActive(false);
    if (!wasHolding) setLightboxOpen(true); // hızlı bas-bırak = tıklama = lightbox
  };

  const handleMouseLeave = () => {
    clearHoldTimer();
    setMagnifierActive(false);
  };

  const handleTouchEnd = (e) => {
    // Mobilde basılı tutma büyüteci yok (dokunmatik ile hassas takip zor), sadece tıklama -> lightbox.
    e.preventDefault();
    setLightboxOpen(true);
  };

  // Lightbox açıkken Esc tuşuna basınca kapat.
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxOpen]);

  return (
    <>
      <div
        className="relative block cursor-zoom-in select-none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchEnd={handleTouchEnd}
        data-testid="zoomable-image-wrapper"
      >
        <img ref={imgRef} src={src} alt={alt} className={className} draggable={false} />
        {magnifierActive && (
          <div
            className="pointer-events-none absolute rounded-full border-2 border-rose-500 shadow-2xl z-10"
            style={{
              width: LENS_SIZE,
              height: LENS_SIZE,
              left: lensPos.x - LENS_SIZE / 2,
              top: lensPos.y - LENS_SIZE / 2,
              backgroundImage: `url(${src})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: `${imgSize.w * ZOOM_FACTOR}px ${imgSize.h * ZOOM_FACTOR}px`,
              backgroundPosition: `${bgPos.x}px ${bgPos.y}px`,
            }}
            data-testid="zoomable-image-lens"
          />
        )}
      </div>

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightboxOpen(false)}
          data-testid="zoomable-image-lightbox"
        >
          <button
            onClick={() => setLightboxOpen(false)}
            data-testid="zoomable-image-lightbox-close"
            className="absolute top-4 right-4 text-zinc-300 hover:text-white bg-zinc-900/80 rounded-full p-2"
          >
            <X className="w-6 h-6" />
          </button>
          <img src={src} alt={alt} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}